import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {CampaignService} from '../../services/campaign/campaign.service';
import {UserService} from '../../services/user.service';
import {MentionService} from '../../services/mention/mention.service';
import {CampaignSave} from '../../../models/campaign-save';
import {apiEndpoints} from '../../app.constants';
import {
  CampaignInfluencerStatus, CampaignsDetailComponent, CampaignTab,
  TermActionType
} from './campaign-detail.component';
import {Location as NgCommonLocation} from '@angular/common';
import {ModalService} from '../../services/modal/modal.service';
import {ReferenceDataService} from '../../services/reference-data/reference-data.service';
import {Campaign} from '../../../models/campaign';
import {ValidationHandlerService} from '../../core/validation-handler.service';
import {WebUtils} from '../../utils/web.utils';
import {
  EventTrackerService, Page, UserActionType, ActivePages, ActionSource,
  TargetType
} from '../../services/event-tracker/event-tracker.service';
import {InfluencerGroupsService} from '../influencer-groups/influencer-groups.service';
import {CacheService} from '../../services/cache/cache.service';

@Component({
  selector: 'campaigns-add',
  templateUrl: './campaign-detail.component.html',
  styleUrls: ['./campaign-detail.component.scss']
})
export class CampaignsAddComponent extends CampaignsDetailComponent implements OnInit {
  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    translate: TranslateService,
    campaignService: CampaignService,
    groupService: InfluencerGroupsService,
    userService: UserService,
    mentionService: MentionService,
    modalService: ModalService,
    validateErrorHandler: ValidationHandlerService,
    referenceDataService: ReferenceDataService,
    private location: NgCommonLocation,
    eventTracker: EventTrackerService,
    cacheService: CacheService
  ) {
    super(
      router,
      activatedRoute,
      translate,
      campaignService,
      groupService,
      userService,
      mentionService,
      modalService,
      validateErrorHandler,
      referenceDataService,
      eventTracker,
      cacheService,
      translate.instant('campaigns.new_campaign.title')
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.sub = this.activatedRoute.params.subscribe(params => {
      this.tabKey = params['tab-key'] || CampaignTab.TERMS;
    });
    this.sendViewEvent();
  }

  getAddTerms() {
    const selectedTerms = this.selectedCampaignTerms;
    const addTerms = selectedTerms
      .map(x => {
        return {
          termId: x.id,
          action: TermActionType.ADD,
          params: x.hasParams ? this.extractParams(x.params) : null
        };
      });

    return addTerms;
  }

  getAddFiles() {
    return this.materialImages
    .filter(x => x.action === 'add')
    .map(x => {
      const imageLink = apiEndpoints.getUploadFile + '/' + x.id;
      return {
        id: x.id,
        name: x.name,
        thumbnail: imageLink,
        $$hashKey: '',
        fileId: x.id,
        contentType: x.contentType,
        action: 'add',
        downloadLink: imageLink
      };
    });
  }

  public replaceTabState(tabKey: string) {
    this.tabKey = tabKey;

    const params = {
      queryParams: {
        activeAccount: WebUtils.extractActiveAccountFromUrl()
      }
    };

    const url = this.router.createUrlTree(['/campaigns-add', tabKey || CampaignTab.TERMS], params);
    this.location.replaceState(url.toString());
  }

  /**
   * handle to create a new campaign
   */
  public createOrUpdateCampaign() {
    this.isSavingCampaign = true;
    const campaignSaveData: CampaignSave = {
      name: this.campaignName,
      projectType: 'CAMPAIGN_CREATION',
      brief: this.campaignBrief,
      additionalInfo: this.campaignAdditionalInfo,
      keywordIds: this._mentions.map(x => x.id),
      terms: this.getAddTerms(),
      influencerIds: this.campaignInfluencers.filter(influencer =>
          (influencer.campaign_influencer_status !== CampaignInfluencerStatus.NEW || !influencer.is_accepted))
        .map(influencer => influencer.influencer_id),
      advertiserID: this.userService.getCompanyId(),
      needSendEmail: true,
      files: this.getAddFiles()
    };
    this.campaignService.addNewCampaign(campaignSaveData)
      .subscribe(resp => {
        // track event to Segment
        this.eventTracker.trackUserAction(UserActionType.ACTION, Page.CAMPAIGN, {
          id: resp.id,
          name: this.campaignName,
          type: Page.CAMPAIGN
        }, {
          action: UserActionType.CREATE,
          source: ActionSource.PAGE,
          ...campaignSaveData
        }, undefined, {
          type: ActivePages.CAMPAIGN_NEW,
          name: this.campaignName,
          id: resp.id
        });
        this.eventTracker.trackUserAction(UserActionType.CHAM_CAMPAIGN_CREATE);
        super.addNewAcceptedToCampaign(resp.id, this.campaignInfluencers, () => { this.isSavingCampaign = false; });
        this.userService.triggerUpdateActivatedInfluencers();
      }, (err) => {
        this.isSavingCampaign = false;
        if (err.error.data === 'invalid_level') {
          this.hasInvalidAccountLevelError = true;
          this.userService.doInvalidActionByLevel('Add to Campaign', err.error.message);
          super.sendInvalidActionToSegement(this.groupService.extractLimitInfoFromErrorMessage(err.error.message,
            this.campaignInfluencers.length, this.userService.maxCampaignInfluencers, true), {
            campaign_id: 0,
            campaign_name: this.campaignName,
            influencers: campaignSaveData.influencerIds
          });
        } else {
          return this.handleSaveError(err);
        }
      });
  }

  isCampaignDataChanged() {
    const hasChangeName = this.campaignName !== '';
    const hasChangeBrief = this.campaignBrief !== '';
    return this.isDirtyCampaignData || hasChangeName || hasChangeBrief;
  }

  public getCampaign() {
    const newCampaign: Campaign = new Campaign();
    newCampaign.id = 0;
    return newCampaign;
  }

  getCampaignCachingKeys() {
    return [];
  }

  private sendViewEvent() {
    this.eventTracker.trackUserAction(UserActionType.VIEW, Page.CAMPAIGN_SETTING,
      {
        type: TargetType.CAMPAIGN
      }, {
        source: ActionSource.PAGE,
        actionDetails: ActivePages.CAMPAIGN_NEW
      }, undefined, undefined);
  }
}



// WEBPACK FOOTER //
// ./src/app/main/campaigns-add/campaign-add.component.ts